import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Empty,
  Flex,
  Form,
  InputNumber,
  Modal,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { useContext, useState } from 'react';

import ProductImage from '../../../../components/productImage';
import { formatCurrency } from '../../../../utils/format';
import { AddOrderReturnContext } from '../addOrderReturnContext';
import ModalSelectProduct from './ModalSelectProduct';

const { confirm } = Modal;
const { Text } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 280;

/** Тоог тооцоололд оруулахад NaN гарахаас хамгаална */
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const Product = ({ productType }) => {
  const [visible, setVisible] = useState(false);

  const { products, changeTotalUnit, removeProduct } = useContext(AddOrderReturnContext);

  const removeRow = (record) =>
    confirm({
      title: 'Барааг устгах уу?',
      okText: 'Устгах',
      cancelText: 'Цуцлах',
      okButtonProps: { danger: true },
      onOk: () => removeProduct(record._id),
    });

  const columns = [
    {
      key: 'index',
      title: '№',
      width: 48,
      align: 'center',
      render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
    },
    {
      key: 'product',
      title: 'Бараа',
      render: (_, record) => (
        <Space size={12} align="center">
          <ProductImage
            src={record?.productImage || record?.imageCover}
            alt={record?.productTitle ?? 'Барааны зураг'}
            size={IMAGE_SIZE}
            style={{
              objectFit: 'contain',
              borderRadius: 4,
              border: '1px solid #eaecf0',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
            }}
          />
          <Space direction="vertical" size={0} style={{ maxWidth: TITLE_MAX_WIDTH }}>
            <Text style={{ maxWidth: TITLE_MAX_WIDTH }} ellipsis={{ tooltip: record?.productTitle }}>
              {record?.productTitle ?? '-'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {record?.productSKU}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      key: 'price',
      title: 'Нэгжийн үнэ',
      align: 'right',
      width: 140,
      render: (_, record) => formatCurrency(num(record?.productPrice)),
    },
    {
      key: 'delivered',
      title: 'Хүргэсэн тоо',
      align: 'right',
      width: 120,
      render: (_, record) => (record?.totalUnit != null ? `${record.totalUnit} ш` : '-'),
    },
    {
      key: 'totalUnitReturn',
      title: 'Буцаах тоо',
      align: 'right',
      width: 160,
      render: (_, record) => (
        <Form.Item
          name={`totalUnit${record._id}`}
          initialValue={record.totalUnitReturn ?? 1}
          style={{ marginBottom: 0 }}
          rules={[
            { required: true, message: 'Тоо ширхэг оруулна уу!' },
            { pattern: new RegExp(/^\d+(\.\d+)?$/), message: 'Бүхэл тоо оруулна уу!' },
          ]}
        >
          <InputNumber
            placeholder="0"
            addonAfter="ш"
            style={{ width: '100%' }}
            min={1}
            max={record?.totalUnit}
            onChange={(value) => changeTotalUnit(record._id, value)}
          />
        </Form.Item>
      ),
    },
    {
      key: 'total',
      title: 'Нийт үнэ',
      align: 'right',
      width: 150,
      render: (_, record) => (
        <Text strong>
          {formatCurrency(num(record?.productPrice) * num(record?.totalUnitReturn))}
        </Text>
      ),
    },
    {
      key: 'action',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Устгах">
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeRow(record)} />
        </Tooltip>
      ),
    },
  ];

  /** Хүснэгтийн нийлбэр мөр — буцаах тоо, нийт үнэ */
  const renderSummary = (rows = []) => {
    const totalUnit = rows.reduce((sum, row) => sum + num(row?.totalUnitReturn), 0);
    const totalPrice = rows.reduce(
      (sum, row) => sum + num(row?.totalUnitReturn) * num(row?.productPrice),
      0
    );

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={4} align="right">
          <Text type="secondary">Нийт</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
          <Text strong>{`${totalUnit} ш`}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="right">
          <Text strong>{formatCurrency(totalPrice)}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6} />
      </Table.Summary.Row>
    );
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Flex justify="flex-end">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setVisible(true)}>
          Бараа нэмэх
        </Button>
      </Flex>

      {products?.length ? (
        <Table
          columns={columns}
          dataSource={products}
          rowKey={(record, index) => record?._id ?? index}
          size="middle"
          pagination={false}
          summary={renderSummary}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text type="secondary" style={{ fontSize: 13 }}>
              "Бараа нэмэх" товчоор буцаах бараагаа сонгоно уу.
            </Text>
          }
          style={{ margin: 0, padding: '24px 0' }}
        />
      )}

      {visible && (
        <ModalSelectProduct
          visible={visible}
          productType={productType}
          onCancel={() => setVisible(false)}
        />
      )}
    </Space>
  );
};

export default Product;
